// Display mapping for the sensor configuration request history. Kept free of runtime imports so
// it can be unit tested without pulling in the component/api barrels.

export interface ConfigurationRequestStatusMeta {
  label: string
  color: 'success' | 'error' | 'default'
}

export interface ConfigurationRequestAuthorMeta {
  /** Primary line: who triggered the request. */
  label: string
  /** Secondary line: human readable flow the request came from ('-' when the source is unknown). */
  sourceLabel: string
  /** Raw backend source value, shown in a tooltip so support can match it with the logs. */
  source: string
}

interface ConfigurationRequestAuthor {
  authorType?: string | null
  userName?: string | null
  source?: string | null
}

interface ConfigurationRequestErrorDetails {
  message?: string | null
  httpStatus?: number | null
}

export const getConfigurationRequestStatusMeta = (status?: string | null): ConfigurationRequestStatusMeta => {
  switch (status) {
    case 'sent':
      return { label: 'Successfully sent', color: 'success' }
    case 'error':
      return { label: 'Error', color: 'error' }
    default:
      return { label: status || '-', color: 'default' }
  }
}

export const configurationRequestSourceLabels: Record<string, string> = {
  'admin-configure': 'Default config push',
  'admin-custom-config': 'Custom config',
  'ble-toggle': 'BLE on/off',
  'metrics-handler': 'Metrics handler',
  'metrics-handler-ble': 'Metrics handler (BLE)',
  cli: 'CLI',
  'enable-gyroscope': 'Enable gyroscope',
  'update-battery-monitor-period': 'Update battery monitor period'
}

export const getConfigurationRequestSourceLabel = (source?: string | null): string => {
  if (!source) return '-'
  return configurationRequestSourceLabels[source] || source
}

export const getConfigurationRequestAuthorMeta = (request: ConfigurationRequestAuthor): ConfigurationRequestAuthorMeta => {
  const { authorType, userName, source } = request
  const sourceLabel = getConfigurationRequestSourceLabel(source)
  const meta = { sourceLabel, source: source || '' }

  switch (authorType) {
    case 'admin':
      return { ...meta, label: userName || 'Admin' }
    case 'automatic':
      return { ...meta, label: 'Automated config request' }
    case 'script':
      // The script name comes from the source, already rendered as the cell subtitle.
      return { ...meta, label: 'Script' }
    default:
      return { ...meta, label: authorType || '-' }
  }
}

export const getConfigurationRequestErrorSummary = (errorDetails?: ConfigurationRequestErrorDetails | null): string => {
  if (!errorDetails) return ''

  const { httpStatus, message } = errorDetails
  const summary = [httpStatus ? `HTTP ${httpStatus}` : '', message || ''].filter(Boolean).join(': ')

  return summary || 'Unknown error'
}
